import { useState } from 'react';
import { fetchPostApiIntroducerPortalIntroducerApplicationOfferAccept } from '@iwoca/lapi-client/introducer_portal';
import { Button, Message } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import { Offer } from 'components/SummaryCard/SummaryCard.helpers';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useReviewOffer } from '../ReviewOffer.context';
import styles from './ConfirmOffer.module.css';
import { ContactTimes } from './ContactTimes/ContactTimes';

type TProps = {
  selectedOffer: TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number];
  isRepeat?: boolean;
};

export const ConfirmOffer = ({ selectedOffer, isRepeat }: TProps) => {
  const { trackEvent } = useAmplitude();
  const { introducerId } = useIntroducerContext();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [contactDateTime, setContactDateTime] = useState<string | null>(null);
  const [asapTimeSelected, setAsapTimeSelected] = useState<boolean>(true);
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>();

  const { selectedApplication, setOfferDecision, setSelectedOfferId } =
    useReviewOffer();

  const { offer_id, product_type, max_amount, timeout } = selectedOffer;

  const handleBackClick = () => {
    setOfferDecision(Offer.UNDECIDED);
    setSelectedOfferId('');
  };

  const handleConfirmation = async () => {
    if (!introducerId) {
      return;
    }

    setHasError(false);
    setIsLoading(true);
    try {
      await fetchPostApiIntroducerPortalIntroducerApplicationOfferAccept({
        introducerId,
        applicationId: selectedApplication.application_uid,
        offerId: offer_id,
        body: {
          data: isRepeat
            ? {}
            : {
                suggested_contact_asap: asapTimeSelected,
                suggested_contact_time: contactDateTime || undefined,
                suggested_contact_phone_number:
                  phoneNumberValue ||
                  selectedApplication.customer.primary_phone,
              },
        },
      });

      trackEvent(AMPLITUDE_EVENTS.offerAgreed, {
        Commission: selectedOffer.withdrawal_fee_percentage || '',
        'Product type': product_type,
        'Offered amount': max_amount,
        'Contact time': isRepeat
          ? 'not selected - is repeat'
          : asapTimeSelected
            ? 'ASAP'
            : contactDateTime,
      });

      setHasError(false);
      window.location.reload();
    } catch {
      setHasError(true);
    }
    setIsLoading(false);
  };

  const renderContent = () => {
    if (isRepeat) {
      return (
        <>
          <h2 className={styles.heading}>
            <Trans>This applicant has previously had a loan with iwoca</Trans>
          </h2>
          <p className={styles.message}>
            <Trans>
              We'll email them to confirm the chosen offer and invite them to
              drawdown on the funds using their iwoca login.
            </Trans>
          </p>
        </>
      );
    }

    if (!timeout) {
      return null;
    }

    return (
      <ContactTimes
        timeout={timeout}
        originalPhoneNumber={selectedApplication?.customer?.primary_phone}
        phoneNumberValue={phoneNumberValue}
        setPhoneNumberValue={setPhoneNumberValue}
        setAsapTimeSelected={setAsapTimeSelected}
        setContactDateTime={setContactDateTime}
      />
    );
  };

  return (
    <>
      {renderContent()}
      <div className={styles.buttonContainer}>
        <Button
          className={styles.confirmButton}
          variant="primary"
          disabled={isLoading}
          onClick={handleConfirmation}
        >
          <Trans>Confirm this offer</Trans>
        </Button>
        <Button onClick={handleBackClick} variant="tertiary">
          <Trans>Go back</Trans>
        </Button>
      </div>
      {hasError && <Message message={t`Something went wrong`} type="error" />}
    </>
  );
};
